import { useEffect, useState, useCallback } from "react";
import api from "../api/client";
import { Alert } from "../components/Ui";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/users?q=${encodeURIComponent(q)}&page=${page}`)
      .then((r) => {
        setUsers(r.data.users);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      }).catch(() => {});
  }, [q, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [q]);

  async function toggleBlock(u) {
    const block = u.block !== "yes";
    if (!window.confirm(`${block ? "Block" : "Unblock"} ${u.email}?`)) return;
    await api.post(`/admin/users/${u._id}/block`, { block });
    load();
  }

  async function toggleReseller(u) {
    const reseller = u.reseller !== "yes";
    if (!window.confirm(`${reseller ? "Make" : "Demote"} ${u.fname} as reseller?`)) return;
    await api.post(`/admin/users/${u._id}/reseller`, { reseller });
    load();
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Users</h1>
        <span className="muted small">{total} total</span>
      </div>

      <input className="input" style={{ maxWidth: 340, marginBottom: 16 }} placeholder="Search email, name or mobile"
        value={q} onChange={(e) => setQ(e.target.value)} />

      <div className="list">
        {users.map((u) => (
          <div key={u._id} className="row" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="grow" style={{ minWidth: 200 }}>
              <div className="truncate" style={{ fontWeight: 700 }}>
                {u.fname}
                {u.isAdmin && <span className="badge" style={{ marginLeft: 8, background: "rgba(109,92,246,.2)", color: "var(--brand)" }}>Admin</span>}
                {u.reseller === "yes" && <span className="badge refunded" style={{ marginLeft: 8 }}>Reseller</span>}
                {u.block === "yes" && <span className="badge review" style={{ marginLeft: 8 }}>Blocked</span>}
              </div>
              <div className="muted small">{u.email} · {u.mobile}</div>
            </div>
            <div className="tabular" style={{ fontWeight: 800, minWidth: 80, textAlign: "right" }}>₹{Number(u.balance).toFixed(2)}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="btn ghost" style={{ padding: "6px 10px", fontSize: 11 }}
                onClick={() => setModal({ user: u, action: "add" })}>+ Add ₹</button>
              <button className="btn ghost" style={{ padding: "6px 10px", fontSize: 11 }}
                onClick={() => setModal({ user: u, action: "deduct" })}>− Deduct ₹</button>
              <button className="btn ghost" style={{ padding: "6px 10px", fontSize: 11 }}
                onClick={() => toggleReseller(u)}>
                {u.reseller === "yes" ? "Demote" : "Reseller"}
              </button>
              <button className={u.block === "yes" ? "btn ghost" : "btn danger"} style={{ padding: "6px 10px", fontSize: 11 }}
                onClick={() => toggleBlock(u)}>
                {u.block === "yes" ? "Unblock" : "Block"}
              </button>
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="muted" style={{ padding: 24 }}>No users found.</p>}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 20 }}>
          <button className="btn ghost" style={{ padding: "8px 16px", fontSize: 13 }}
            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Previous
          </button>
          <span className="muted small">
            Page {page} of {pages}
          </span>
          <button className="btn ghost" style={{ padding: "8px 16px", fontSize: 13 }}
            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      )}

      {modal && <WalletModal {...modal} onClose={() => { setModal(null); load(); }} />}
    </div>
  );
}

function WalletModal({ user, action, onClose }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/admin/users/${user._id}/wallet`, {
        action,
        amount: Number(amount),
        reason,
      });
      setDone(`Done. New balance: ₹${Number(data.balance).toFixed(2)}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card" style={{ padding: 24, maxWidth: 400, width: "90%" }}>
        <h2 style={{ margin: "0 0 4px" }}>{action === "add" ? "Add to wallet" : "Deduct from wallet"}</h2>
        <p className="muted small" style={{ marginBottom: 16 }}>{user.fname} · {user.email} · Current: ₹{Number(user.balance).toFixed(2)}</p>

        {done ? (
          <div>
            <p style={{ color: "var(--ok)", fontWeight: 700, marginBottom: 16 }}>{done}</p>
            <button className="btn primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <div>
            <label className="muted small" style={{ display: "block", marginBottom: 4 }}>Amount (₹)</label>
            <input className="input tabular" type="number" min="1" step="1" value={amount}
              onChange={(e) => setAmount(e.target.value)} style={{ marginBottom: 12, width: "100%" }} />

            <label className="muted small" style={{ display: "block", marginBottom: 4 }}>Reason (optional)</label>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Bonus, correction, penalty" style={{ marginBottom: 16, width: "100%" }} />

            <Alert>{error}</Alert>
            <div style={{ display: "flex", gap: 8 }}>
              <button className={action === "add" ? "btn primary" : "btn danger"} onClick={submit} disabled={busy || !(Number(amount) > 0)}>
                {busy ? "Processing…" : action === "add" ? `Add ₹${amount || 0}` : `Deduct ₹${amount || 0}`}
              </button>
              <button className="btn ghost" onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}